import Logger from './logger';
import ErrorTracker from './errorTracker';
import { LOCAL_DEV_CORS_ORIGINS } from '../constants/localDevOrigins';
import { isNetworkFailureNotCors, isSameOriginFetchUrl } from '../utils/corsRequestClassification';

// CORS configuration
interface CORSConfig {
  allowedOrigins: string[];
  allowedMethods: string[];
  allowedHeaders: string[];
  allowCredentials: boolean;
  maxAge: number;
  enablePreflight: boolean;
  strictMode: boolean;
  environment: 'development' | 'production' | 'staging';
}

// CORS request
interface CORSRequest {
  id: string;
  timestamp: number;
  origin: string;
  method: string;
  headers: string[];
  url: string;
  allowed: boolean;
  reason?: string;
  responseHeaders?: Record<string, string>;
}

// CORS statistics
interface CORSStats {
  totalRequests: number;
  allowedRequests: number;
  blockedRequests: number;
  requestsByOrigin: Record<string, number>;
  requestsByMethod: Record<string, number>;
  recentRequests: CORSRequest[];
  averageRequestsPerHour: number;
  lastRequest: CORSRequest | null;
}

// CORS error
interface CORSError {
  id: string;
  timestamp: number;
  type: 'origin_not_allowed' | 'method_not_allowed' | 'header_not_allowed' | 'credentials_not_supported' | 'preflight_failed';
  message: string;
  origin: string;
  method: string;
  headers: string[];
  url: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  resolvedAt?: number;
  resolvedBy?: string;
}

class CORSService {
  private static instance: CORSService;
  private config: CORSConfig;
  private requests: CORSRequest[] = [];
  private errors: CORSError[] = [];
  private stats: CORSStats;
  private isInitialized: boolean = false;
  private errorListeners: ((error: CORSError) => void)[] = [];

  constructor() {
    this.config = {
      allowedOrigins: [
        'https://tranzit.io',
        'https://www.tranzit.io',
        'https://app.tranzit.io',
        'https://admin.tranzit.io',
        'https://staging.tranzit.io',
        // Localhost only in development
        ...(process.env.NODE_ENV !== 'production' ? [...LOCAL_DEV_CORS_ORIGINS] : []),
      ],
      allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'],
      allowedHeaders: [
        'Content-Type',
        'Authorization',
        'X-Requested-With',
        'Accept',
        'Origin',
        'Access-Control-Request-Method',
        'Access-Control-Request-Headers',
        'X-API-Key',
        'X-User-ID',
        'X-Session-ID'
      ],
      allowCredentials: true,
      maxAge: 86400, // 24 hours
      enablePreflight: true,
      strictMode: false,
      environment: process.env.NODE_ENV === 'production' ? 'production' : 'development'
    };

    this.stats = {
      totalRequests: 0,
      allowedRequests: 0,
      blockedRequests: 0,
      requestsByOrigin: {},
      requestsByMethod: {},
      recentRequests: [],
      averageRequestsPerHour: 0,
      lastRequest: null
    };
  }

  public static getInstance(): CORSService {
    if (!CORSService.instance) {
      CORSService.instance = new CORSService();
    }
    return CORSService.instance;
  }

  /**
   * Initialize CORS service
   */
  public initialize(config: Partial<CORSConfig> = {}): void {
    try {
      this.config = { ...this.config, ...config };
      this.setupCORSMonitoring();
      this.isInitialized = true;

      Logger.info('CORS Service initialized', {
        allowedOrigins: this.config.allowedOrigins.length,
        allowedMethods: this.config.allowedMethods.length,
        allowedHeaders: this.config.allowedHeaders.length,
        environment: this.config.environment
      });

      ErrorTracker.trackDataProtectionEvent(
        'cors_service_initialized',
        'internal',
        undefined,
        { 
          allowedOrigins: this.config.allowedOrigins.length,
          environment: this.config.environment 
        }
      );

    } catch (error) {
      Logger.error('Failed to initialize CORS Service', error instanceof Error ? error : new Error('Unknown error'));
      ErrorTracker.trackError(
        error instanceof Error ? error : new Error('CORS Service initialization failed'),
        'CORSService',
        { component: 'CORSService', action: 'initialize' }
      );
    }
  }

  /**
   * Set up CORS monitoring
   */
  private setupCORSMonitoring(): void {
    try {
      const self = this;
      // Monitor app API / Cloud Run / Functions only. Skip Firestore Listen keepalives and Auth
      // token traffic — wrapping those flooded prod consoles and grew an unbounded request buffer.
      const originalFetch = window.fetch;
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
        if (!self.shouldMonitorCorsUrl(url)) {
          return originalFetch(input, init);
        }

        const request = self.createCORSRequest(input, init);
        self.recordCORSRequest(request);

        try {
          const response = await originalFetch(input, init);
          self.handleCORSResponse(request, response);
          return response;
        } catch (error) {
          if (isNetworkFailureNotCors(error)) {
            Logger.warn('API network failure (not CORS)', {
              url: request.url,
              method: request.method,
              message: error instanceof Error ? error.message : String(error),
            });
            throw error;
          }
          self.handleCORSError(request, error);
          throw error;
        }
      };

      // Monitor XMLHttpRequest (same URL filter as fetch)
      const originalXHROpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, user?: string | null, password?: string | null) {
        const urlString = typeof url === 'string' ? url : url.href;
        if (CORSService.getInstance().shouldMonitorCorsUrl(urlString)) {
          const request = CORSService.getInstance().createCORSRequest(url, { method });
          CORSService.getInstance().recordCORSRequest(request);
        }
        return originalXHROpen.call(this, method, url, async ?? true, user, password);
      };

      Logger.info('CORS monitoring set up');

    } catch (error) {
      Logger.error('Failed to set up CORS monitoring', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * URLs worth CORS monitoring in production (app API + Cloud Functions/Run).
   * Excludes Firestore/Auth SDK streams — those are not CORS policy failures and poll continuously.
   */
  private shouldMonitorCorsUrl(url: string): boolean {
    try {
      const normalized = url.trim();
      if (!normalized) return false;
      if (normalized.includes('firestore.googleapis.com') || normalized.includes('firebaseio.com')) {
        return false;
      }
      if (
        normalized.includes('identitytoolkit.googleapis.com') ||
        normalized.includes('securetoken.googleapis.com')
      ) {
        return false;
      }
      // Same-origin (relative or same host)
      if (normalized.startsWith('/') || normalized.startsWith('./')) return true;
      if (typeof window !== 'undefined' && window.location?.origin && normalized.startsWith(window.location.origin)) {
        return true;
      }
      // Backend API (common dev/prod patterns)
      if (normalized.includes('localhost:3001') || normalized.includes('127.0.0.1:3001')) return true;
      if (normalized.includes('tranzit.io') && (normalized.includes('/api') || normalized.includes('api.'))) {
        return true;
      }
      if (normalized.includes('cloudfunctions.net') || normalized.includes('.run.app')) return true;
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Create CORS request from fetch input
   */
  private createCORSRequest(input: RequestInfo | URL, init?: RequestInit): CORSRequest {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method || 'GET';
    const headers = init?.headers ? Object.keys(init.headers as Record<string, string>) : [];
    const origin = window.location.origin;

    return {
      id: `cors_request_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      origin,
      method,
      headers,
      url,
      allowed: false
    };
  }

  /**
   * Record CORS request
   */
  private recordCORSRequest(request: CORSRequest): void {
    try {
      // Check if request is allowed
      const isAllowed = this.checkCORSRequest(request);
      request.allowed = isAllowed.allowed;
      request.reason = isAllowed.reason;

      // Cap retained requests — only blocked entries need a durable trail for the security dashboard.
      if (!request.allowed) {
        this.requests.push(request);
        if (this.requests.length > 100) {
          this.requests = this.requests.slice(-100);
        }
        this.recordCORSError(request, isAllowed.reason || 'Unknown CORS error');
      }
      this.updateStats(request);

    } catch (error) {
      Logger.error('Failed to record CORS request', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Check if CORS request is allowed
   */
  private checkCORSRequest(request: CORSRequest): { allowed: boolean; reason?: string } {
    try {
      if (isSameOriginFetchUrl(request.url, request.origin)) {
        return { allowed: true };
      }

      // Check origin
      if (!this.isOriginAllowed(request.origin)) {
        return { allowed: false, reason: 'Origin not allowed' };
      }

      // Check method
      if (!this.isMethodAllowed(request.method)) {
        return { allowed: false, reason: 'Method not allowed' };
      }

      // Check headers
      if (!this.areHeadersAllowed(request.headers)) {
        return { allowed: false, reason: 'Headers not allowed' };
      }

      // Check if it's a cross-origin request
      const requestUrl = new URL(request.url, `${request.origin}/`);
      const isCrossOrigin = requestUrl.origin !== request.origin;

      if (isCrossOrigin && this.config.strictMode) {
        // Additional strict checks for cross-origin requests
        if (!this.isSecureOrigin(request.origin)) {
          return { allowed: false, reason: 'Non-secure origin not allowed in strict mode' };
        }
      }

      return { allowed: true };

    } catch (error) {
      Logger.error('Failed to check CORS request', error instanceof Error ? error : new Error('Unknown error'));
      return { allowed: false, reason: 'CORS check failed' };
    }
  }

  /**
   * Check if origin is allowed
   */
  private isOriginAllowed(origin: string): boolean {
    if (this.config.allowedOrigins.includes('*')) {
      return true;
    }

    return this.config.allowedOrigins.some(allowedOrigin => {
      if (allowedOrigin.includes('*')) {
        const pattern = allowedOrigin.replace(/\*/g, '.*');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(origin);
      }
      return allowedOrigin === origin;
    });
  }

  /**
   * Check if method is allowed
   */
  private isMethodAllowed(method: string): boolean {
    return this.config.allowedMethods.includes(method.toUpperCase());
  }

  /**
   * Check if headers are allowed
   */
  private areHeadersAllowed(headers: string[]): boolean {
    return headers.every(header => 
      this.config.allowedHeaders.includes(header) || 
      header.toLowerCase().startsWith('x-') // Allow custom headers
    );
  }

  /**
   * Check if origin is secure
   */
  private isSecureOrigin(origin: string): boolean {
    return origin.startsWith('https://') || origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1');
  }

  /**
   * Handle CORS response
   */
  private handleCORSResponse(request: CORSRequest, response: Response): void {
    try {
      // Check for CORS headers in response
      const corsHeaders: Record<string, string> = {};
      const origin = response.headers.get('Access-Control-Allow-Origin');
      const methods = response.headers.get('Access-Control-Allow-Methods');
      const headers = response.headers.get('Access-Control-Allow-Headers');
      const credentials = response.headers.get('Access-Control-Allow-Credentials');

      if (origin) corsHeaders['Access-Control-Allow-Origin'] = origin;
      if (methods) corsHeaders['Access-Control-Allow-Methods'] = methods;
      if (headers) corsHeaders['Access-Control-Allow-Headers'] = headers;
      if (credentials) corsHeaders['Access-Control-Allow-Credentials'] = credentials;

      request.responseHeaders = corsHeaders;
    } catch (error) {
      Logger.error('Failed to handle CORS response', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Handle CORS error
   */
  private handleCORSError(request: CORSRequest, error: any): void {
    try {
      const errorMessage = error.message || 'Unknown CORS error';
      this.recordCORSError(request, errorMessage);

    } catch (err) {
      Logger.error('Failed to handle CORS error', err instanceof Error ? err : new Error('Unknown error'));
    }
  }

  /**
   * Record CORS error
   */
  private recordCORSError(request: CORSRequest, message: string): void {
    try {
      const error: CORSError = {
        id: `cors_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        type: this.determineCORSErrorType(request, message),
        message,
        origin: request.origin,
        method: request.method,
        headers: request.headers,
        url: request.url,
        severity: this.determineCORSErrorSeverity(request, message),
        resolved: false
      };

      this.errors.push(error);
      this.notifyErrorListeners(error);

      Logger.warn('CORS error recorded', {
        id: error.id,
        type: error.type,
        origin: error.origin,
        method: error.method,
        severity: error.severity
      });

      ErrorTracker.trackDataProtectionEvent(
        'cors_error_recorded',
        error.severity,
        undefined,
        {
          type: error.type,
          origin: error.origin,
          method: error.method,
          severity: error.severity
        }
      );

    } catch (err) {
      Logger.error('Failed to record CORS error', err instanceof Error ? err : new Error('Unknown error'));
    }
  }

  /**
   * Determine CORS error type
   */
  private determineCORSErrorType(request: CORSRequest, message: string): CORSError['type'] {
    if (message.includes('origin')) return 'origin_not_allowed';
    if (message.includes('method')) return 'method_not_allowed';
    if (message.includes('header')) return 'header_not_allowed';
    if (message.includes('credentials')) return 'credentials_not_supported';
    if (message.includes('preflight')) return 'preflight_failed';
    return 'origin_not_allowed';
  }

  /**
   * Determine CORS error severity
   */
  private determineCORSErrorSeverity(request: CORSRequest, message: string): 'low' | 'medium' | 'high' | 'critical' {
    if (message.includes('credentials') || message.includes('preflight')) return 'high';
    if (message.includes('method') || message.includes('header')) return 'medium';
    return 'low';
  }

  /**
   * Update statistics
   */
  private updateStats(request: CORSRequest): void {
    this.stats.totalRequests++;
    
    if (request.allowed) {
      this.stats.allowedRequests++;
    } else {
      this.stats.blockedRequests++;
    }

    // Update requests by origin
    this.stats.requestsByOrigin[request.origin] = (this.stats.requestsByOrigin[request.origin] || 0) + 1;
    
    // Update requests by method
    this.stats.requestsByMethod[request.method] = (this.stats.requestsByMethod[request.method] || 0) + 1;
    
    // Update recent requests
    this.stats.recentRequests.unshift(request);
    if (this.stats.recentRequests.length > 100) {
      this.stats.recentRequests = this.stats.recentRequests.slice(0, 100);
    }
    
    // Update last request
    this.stats.lastRequest = request;
    
    // Calculate average requests per hour
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const recentRequests = this.requests.filter(r => r.timestamp > oneHourAgo);
    this.stats.averageRequestsPerHour = recentRequests.length;
  }

  /**
   * Notify error listeners
   */
  private notifyErrorListeners(error: CORSError): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        Logger.error('Error in CORS error listener', err instanceof Error ? err : new Error('Unknown error'));
      }
    });
  }

  /**
   * Add error listener
   */
  public addErrorListener(listener: (error: CORSError) => void): void {
    this.errorListeners.push(listener);
  }

  /**
   * Remove error listener
   */
  public removeErrorListener(listener: (error: CORSError) => void): void {
    const index = this.errorListeners.indexOf(listener);
    if (index > -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  /**
   * Get requests
   */
  public getRequests(filter?: {
    origin?: string;
    method?: string;
    allowed?: boolean;
    startTime?: number;
    endTime?: number;
  }): CORSRequest[] {
    let filteredRequests = [...this.requests];

    if (filter) {
      if (filter.origin) {
        filteredRequests = filteredRequests.filter(r => r.origin === filter.origin);
      }
      if (filter.method) {
        filteredRequests = filteredRequests.filter(r => r.method === filter.method);
      }
      if (filter.allowed !== undefined) {
        filteredRequests = filteredRequests.filter(r => r.allowed === filter.allowed);
      }
      if (filter.startTime) {
        filteredRequests = filteredRequests.filter(r => r.timestamp >= filter.startTime!);
      }
      if (filter.endTime) {
        filteredRequests = filteredRequests.filter(r => r.timestamp <= filter.endTime!);
      }
    }

    return filteredRequests.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Get errors
   */
  public getErrors(filter?: {
    type?: string;
    severity?: string;
    resolved?: boolean;
    startTime?: number;
    endTime?: number;
  }): CORSError[] {
    let filteredErrors = [...this.errors];

    if (filter) {
      if (filter.type) {
        filteredErrors = filteredErrors.filter(e => e.type === filter.type);
      }
      if (filter.severity) {
        filteredErrors = filteredErrors.filter(e => e.severity === filter.severity);
      }
      if (filter.resolved !== undefined) {
        filteredErrors = filteredErrors.filter(e => e.resolved === filter.resolved);
      }
      if (filter.startTime) {
        filteredErrors = filteredErrors.filter(e => e.timestamp >= filter.startTime!);
      }
      if (filter.endTime) {
        filteredErrors = filteredErrors.filter(e => e.timestamp <= filter.endTime!);
      }
    }

    return filteredErrors.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Get statistics
   */
  public getStats(): CORSStats {
    return { ...this.stats };
  }

  /**
   * Resolve error
   */
  public resolveError(errorId: string, resolvedBy: string): boolean {
    const error = this.errors.find(e => e.id === errorId);
    if (error) {
      error.resolved = true;
      error.resolvedAt = Date.now();
      error.resolvedBy = resolvedBy;
      
      Logger.info('CORS error resolved', { errorId, resolvedBy });
      return true;
    }
    return false;
  }

  /**
   * Clear requests and errors
   */
  public clearData(): void {
    this.requests = [];
    this.errors = [];
    this.stats = {
      totalRequests: 0,
      allowedRequests: 0,
      blockedRequests: 0,
      requestsByOrigin: {},
      requestsByMethod: {},
      recentRequests: [],
      averageRequestsPerHour: 0,
      lastRequest: null
    };
    
    Logger.info('CORS data cleared');
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<CORSConfig>): void {
    this.config = { ...this.config, ...newConfig };
    Logger.info('CORS configuration updated');
  }

  /**
   * Get current configuration
   */
  public getConfig(): CORSConfig {
    return { ...this.config };
  }

  /**
   * Check if service is initialized
   */
  public isServiceInitialized(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
const corsService = new CORSService();
export default corsService;
export { type CORSConfig, type CORSRequest, type CORSStats, type CORSError };
